import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Loader2, TerminalSquare } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { findSshProfileByReference } from '@/OS/lib/sshProfiles';
import type { SshProfile } from '@/OS/types/ssh';
import { useKnowledgeBases } from '@/hooks/useKnowledgeBases';
import { buildMapleMythosKnowledgeBaseDescription, buildMapleMythosKnowledgeBaseDocuments, buildMapleMythosKnowledgeBaseName } from '@/lib/demythos/knowledgeBase';
import { isBillableMapleMythosModel } from '@/lib/demythos/creditMetering';
import { mapleMythosRuntime as deMythosRuntime } from '@/lib/demythos/runtime';
import type {
  MapleMythosReasoningMode,
  MapleMythosReturnMode,
  MapleMythosRunState,
  MapleMythosSshGrant,
  MapleMythosTerminalResult,
} from '@/lib/demythos/types';
import type { AIModel } from '@/types';
import { SHIMMER_INTERVAL_MS, buildSessionMeta, buildThinkingFrame } from '../maplecode/claudeBrowserCompat';
import ConsoleMarkdown from '../maplecode/ConsoleMarkdown';
import { DEMYTHOS_HELP_LINES, formatTimeBudget, parseDeMythosCommand } from './DeMythosCommands';

interface DeMythosConsoleProps {
  models: AIModel[];
  onExit?: () => void;
  initialPrompt?: string;
  sshProfiles?: SshProfile[];
  runTerminalCommand?: (command: string) => Promise<MapleMythosTerminalResult>;
  submitTerminalInput?: (input: string) => Promise<MapleMythosTerminalResult>;
}

type SessionEntry = {
  id: string;
  role: 'system' | 'user' | 'tool';
  content: string;
  label?: string;
};

type PendingBridgePromptState = {
  prompt: string;
  secret?: boolean;
};

type StoredDeMythosSettings = {
  selectedModelId?: string;
  maxLoops?: number;
  tokenBudget?: string;
  creditBudget?: string;
  timeBudgetSeconds?: string;
  reasoningMode?: MapleMythosReasoningMode;
  returnMode?: MapleMythosReturnMode;
  grantedSshProfileId?: string;
  knowledgeBaseAutoSave?: boolean;
  ultraModeEnabled?: boolean;
};

const cloudProviders = new Set(['openai', 'gemini', 'deepseek', 'cohere', 'maplept']);
const INPUT_HISTORY_KEY = 'demythos_input_history';
const SETTINGS_KEY = 'demythos_settings_v1';
const MAX_INPUT_HISTORY = 100;
const ULTRA_MAX_LOOPS = 50;
const FINAL_RUN_STATUSES = new Set(['completed', 'failed', 'stopped']);

function createId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  return `demythos-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function createEntry(role: SessionEntry['role'], content: string, label?: string): SessionEntry {
  return {
    id: createId(),
    role,
    content,
    label,
  };
}

function defaultModelId(models: AIModel[]) {
  return models.find((model) => model.tier === 'free')?.id || models[0]?.id || '';
}

function defaultPrompt() {
  return 'Analyze the task, critique your own reasoning, and keep refining until the answer is strong enough to ship.';
}

function getStoredInputHistory() {
  try {
    const raw = localStorage.getItem(INPUT_HISTORY_KEY);
    if (!raw) return [] as string[];

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [] as string[];

    return parsed
      .map((value) => String(value || '').trim())
      .filter(Boolean)
      .slice(-MAX_INPUT_HISTORY);
  } catch {
    return [] as string[];
  }
}

function persistInputHistory(history: string[]) {
  try {
    localStorage.setItem(INPUT_HISTORY_KEY, JSON.stringify(history.slice(-MAX_INPUT_HISTORY)));
  } catch {
    // ignore storage errors in browser-restricted contexts
  }
}

function getStoredSettings(): StoredDeMythosSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return {};

    const parsed = JSON.parse(raw) as StoredDeMythosSettings;
    return {
      selectedModelId: typeof parsed.selectedModelId === 'string' ? parsed.selectedModelId : undefined,
      maxLoops: typeof parsed.maxLoops === 'number' ? parsed.maxLoops : undefined,
      tokenBudget: typeof parsed.tokenBudget === 'string' ? parsed.tokenBudget : undefined,
      creditBudget: typeof parsed.creditBudget === 'string' ? parsed.creditBudget : undefined,
      timeBudgetSeconds: typeof parsed.timeBudgetSeconds === 'string' ? parsed.timeBudgetSeconds : undefined,
      grantedSshProfileId: typeof parsed.grantedSshProfileId === 'string' ? parsed.grantedSshProfileId : undefined,
      knowledgeBaseAutoSave: typeof parsed.knowledgeBaseAutoSave === 'boolean' ? parsed.knowledgeBaseAutoSave : undefined,
      ultraModeEnabled: typeof parsed.ultraModeEnabled === 'boolean' ? parsed.ultraModeEnabled : undefined,
      reasoningMode: ['balanced', 'skeptical', 'expansive'].includes(String(parsed.reasoningMode))
        ? parsed.reasoningMode
        : undefined,
      returnMode: ['final-answer', 'summary-answer', 'full-state'].includes(String(parsed.returnMode))
        ? parsed.returnMode
        : undefined,
    };
  } catch {
    return {};
  }
}

function persistSettings(settings: StoredDeMythosSettings) {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // ignore storage errors in browser-restricted contexts
  }
}

function formatCreditAmount(value: number) {
  if (!Number.isFinite(value) || value <= 0) {
    return '0';
  }

  if (value < 0.0001) {
    return '<0.0001';
  }

  if (value < 0.01) {
    return value.toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
  }

  return value.toFixed(4);
}

function isPhaseStartEntry(entry: MapleMythosRunState['transcript'][number]) {
  return Boolean(entry.phase) && entry.kind === 'phase' && entry.message === `${entry.phase} phase started.`;
}

function isPhaseCompletionEntry(entry: MapleMythosRunState['transcript'][number], phase: string | undefined, loopIndex: number | undefined) {
  if (!phase) {
    return false;
  }

  return entry.phase === phase && entry.loopIndex === loopIndex && entry.message === `${phase} phase completed.`;
}

function toSshGrant(profile: SshProfile | null): MapleMythosSshGrant | undefined {
  if (!profile) return undefined;

  return {
    profileId: profile.id,
    name: profile.name,
    host: profile.host,
    port: profile.port,
    username: profile.username,
  };
}

function formatSshGrant(grant?: MapleMythosSshGrant) {
  if (!grant) return 'none';
  return `${grant.name} (${grant.username}@${grant.host}:${grant.port})`;
}

function buildStatusLine(run: MapleMythosRunState | null, activeRunModel: AIModel | null, grantedSshProfile: SshProfile | null) {
  if (!run) {
    return `No active run · ssh=${grantedSshProfile ? grantedSshProfile.name : 'none'}`;
  }

  const creditsLabel = activeRunModel && !isBillableMapleMythosModel(activeRunModel)
    ? 'non-billable'
    : formatCreditAmount(run.creditsUsed);

  return [
    `run=${run.id}`,
    `status=${run.status}`,
    `loop=${run.currentLoop}/${run.maxLoops}`,
    `model=${run.modelName}`,
    `mode=${run.executionMode}`,
    `tokens=${run.tokensUsed}`,
    `credits=${creditsLabel}`,
    `ssh=${run.sshGrant?.name || grantedSshProfile?.name || 'none'}`,
  ].join(' · ');
}

function summarizeResult(run: MapleMythosRunState | null) {
  if (!run) {
    return 'No result available yet.';
  }

  return [
    `summary: ${run.finalSummary || 'No summary yet.'}`,
    `answer: ${run.finalAnswer || run.workingState.latestAnswer || 'No answer yet.'}`,
  ].join('\n');
}

function buildActivePrompt(prompt: string, fallback: string) {
  const trimmed = prompt.trim();
  return trimmed || fallback;
}

export function DeMythosConsole({
  models,
  onExit,
  initialPrompt,
  sshProfiles = [],
  runTerminalCommand,
  submitTerminalInput,
}: DeMythosConsoleProps) {
  const storedSettings = getStoredSettings();
  const seededPrompt = initialPrompt?.trim() || defaultPrompt();
  const { createKnowledgeBase, addDocument: addKnowledgeBaseDocument } = useKnowledgeBases();

  const [prompt, setPrompt] = useState(seededPrompt);
  const [selectedModelId, setSelectedModelId] = useState(() => storedSettings.selectedModelId || defaultModelId(models));
  const [maxLoops, setMaxLoops] = useState(storedSettings.maxLoops || 6);
  const [tokenBudget, setTokenBudget] = useState(storedSettings.tokenBudget || '6000');
  const [creditBudget, setCreditBudget] = useState(storedSettings.creditBudget || '0.5');
  const [timeBudgetSeconds, setTimeBudgetSeconds] = useState(storedSettings.timeBudgetSeconds || '90');
  const [reasoningMode, setReasoningMode] = useState<MapleMythosReasoningMode>(storedSettings.reasoningMode || 'balanced');
  const [returnMode, setReturnMode] = useState<MapleMythosReturnMode>(storedSettings.returnMode || 'full-state');
  const [grantedSshProfileId, setGrantedSshProfileId] = useState(storedSettings.grantedSshProfileId || '');
  const [knowledgeBaseAutoSave, setKnowledgeBaseAutoSave] = useState(Boolean(storedSettings.knowledgeBaseAutoSave));
  const [ultraModeEnabled, setUltraModeEnabled] = useState(Boolean(storedSettings.ultraModeEnabled));
  const [lastKnowledgeBaseName, setLastKnowledgeBaseName] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSavingKnowledgeBase, setIsSavingKnowledgeBase] = useState(false);
  const [isStatusPaneOpen, setIsStatusPaneOpen] = useState(true);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [runs, setRuns] = useState<MapleMythosRunState[]>(() => deMythosRuntime.listRuns());
  const [activeRunId, setActiveRunId] = useState<string | null>(deMythosRuntime.listRuns()[0]?.id || null);
  const [clearedTranscriptCutoffs, setClearedTranscriptCutoffs] = useState<Record<string, number>>({});
  const [pendingBridgePrompt, setPendingBridgePrompt] = useState<PendingBridgePromptState | null>(null);
  const [sessionEntries, setSessionEntries] = useState<SessionEntry[]>(() => {
    const entries = [
      createEntry('system', 'DeMythos started.', 'session'),
      createEntry('system', 'Type /help for commands, or type a prompt directly to start a reasoning run.', 'session'),
    ];

    if (initialPrompt?.trim()) {
      entries.push(createEntry('system', `Boot prompt loaded: ${initialPrompt.trim()}`, 'boot'));
    }

    return entries;
  });
  const [thinkingTick, setThinkingTick] = useState(0);

  const transcriptEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputHistoryRef = useRef<string[]>(getStoredInputHistory());
  const historyDraftRef = useRef('');
  const bootPromptConsumedRef = useRef(false);
  const pendingBridgePromptResolverRef = useRef<((value: string) => void) | null>(null);
  const autoSavePendingRunIdsRef = useRef(new Set<string>());
  const autoSaveInFlightRunIdsRef = useRef(new Set<string>());

  useEffect(() => {
    const unsubscribe = deMythosRuntime.subscribe((nextRuns) => {
      setRuns(nextRuns);
      setActiveRunId((current) => {
        if (current && nextRuns.some((run) => run.id === current)) {
          return current;
        }

        return nextRuns[0]?.id || null;
      });
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!models.length) {
      return;
    }

    const selectedStillExists = models.some((model) => model.id === selectedModelId);
    if (!selectedStillExists) {
      setSelectedModelId(defaultModelId(models));
    }
  }, [models, selectedModelId]);

  useEffect(() => {
    const grantedStillExists = !grantedSshProfileId || sshProfiles.some((profile) => profile.id === grantedSshProfileId);
    if (!grantedStillExists) {
      setGrantedSshProfileId('');
    }
  }, [grantedSshProfileId, sshProfiles]);

  useEffect(() => {
    persistSettings({
      selectedModelId,
      maxLoops,
      tokenBudget,
      creditBudget,
      timeBudgetSeconds,
      reasoningMode,
      returnMode,
      grantedSshProfileId: grantedSshProfileId || undefined,
      knowledgeBaseAutoSave,
      ultraModeEnabled,
    });
  }, [selectedModelId, maxLoops, tokenBudget, creditBudget, timeBudgetSeconds, reasoningMode, returnMode, grantedSshProfileId, knowledgeBaseAutoSave, ultraModeEnabled]);

  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [runs, sessionEntries, isLoading]);

  useEffect(() => {
    if (!isLoading) return undefined;

    const timer = window.setInterval(() => {
      setThinkingTick((current) => current + 1);
    }, SHIMMER_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [isLoading]);

  useEffect(() => {
    if (!initialPrompt?.trim() || bootPromptConsumedRef.current || !selectedModelId) {
      return;
    }

    bootPromptConsumedRef.current = true;
    setInput(initialPrompt.trim());
    window.setTimeout(() => {
      void submitPrompt(initialPrompt.trim());
    }, 0);
  }, [initialPrompt, selectedModelId]);

  useEffect(() => {
    const latestKnowledgeBase = runs.find((run) => run.knowledgeBaseSaveStatus === 'saved' && run.knowledgeBaseName)?.knowledgeBaseName;
    if (latestKnowledgeBase) {
      setLastKnowledgeBaseName(latestKnowledgeBase);
    }
  }, [runs]);

  const selectedModel = useMemo(() => models.find((model) => model.id === selectedModelId) || null, [models, selectedModelId]);
  const activeRun = useMemo(() => runs.find((run) => run.id === activeRunId) || null, [runs, activeRunId]);
  const activeRunModel = useMemo(() => models.find((model) => model.id === activeRun?.modelId) || null, [models, activeRun?.modelId]);
  const grantedSshProfile = useMemo(
    () => findSshProfileByReference(sshProfiles, grantedSshProfileId || undefined) || null,
    [sshProfiles, grantedSshProfileId]
  );

  const buildRuntimeDependencies = (sshGrant?: MapleMythosSshGrant) => ({
    appModels: models,
    sshGrant,
    terminalBridge: runTerminalCommand && submitTerminalInput
      ? {
          runCommand: runTerminalCommand,
          submitInput: submitTerminalInput,
        }
      : undefined,
    requestInput: runTerminalCommand && submitTerminalInput ? requestBridgeInput : undefined,
  });

  const visibleRunTranscript = useMemo(() => {
    if (!activeRun) {
      return [] as MapleMythosRunState['transcript'];
    }

    const cutoff = clearedTranscriptCutoffs[activeRun.id];
    if (!cutoff) {
      return activeRun.transcript;
    }

    return activeRun.transcript.filter((entry) => new Date(entry.timestamp).getTime() > cutoff);
  }, [activeRun, clearedTranscriptCutoffs]);

  const visibleLatestResult = useMemo(() => {
    if (!activeRun) {
      return summarizeResult(null);
    }

    const cutoff = clearedTranscriptCutoffs[activeRun.id];
    if (cutoff && new Date(activeRun.updatedAt).getTime() <= cutoff) {
      return 'No result available yet.';
    }

    return summarizeResult(activeRun);
  }, [activeRun, clearedTranscriptCutoffs]);

  const activePhaseEntryIds = useMemo(() => {
    if (!activeRun || activeRun.status !== 'running') {
      return new Set<string>();
    }

    const inProgressIds = new Set<string>();

    activeRun.transcript.forEach((entry, index, transcript) => {
      if (!isPhaseStartEntry(entry)) {
        return;
      }

      const completed = transcript.slice(index + 1).some((candidate) => isPhaseCompletionEntry(candidate, entry.phase, entry.loopIndex));
      if (!completed) {
        inProgressIds.add(entry.id);
      }
    });

    return inProgressIds;
  }, [activeRun]);

  const sessionMeta = useMemo(
    () => buildSessionMeta({
      startedAt: activeRun?.startedAt ? new Date(activeRun.startedAt).getTime() : Date.now(),
      currentPath: `demythos://${activeRun?.id || 'session'}`,
      messageCount: sessionEntries.length + visibleRunTranscript.length,
      modelName: selectedModel?.name || activeRun?.modelName || 'none',
      trustEnabled: true,
    }),
    [activeRun?.id, activeRun?.modelName, activeRun?.startedAt, selectedModel?.name, sessionEntries.length, visibleRunTranscript.length]
  );

  const thinkingFrame = useMemo(
    () => buildThinkingFrame('DeMythos is reasoning…', thinkingTick),
    [thinkingTick]
  );

  const ultraEnabled = ultraModeEnabled;

  const appendEntry = (role: SessionEntry['role'], content: string, label?: string) => {
    setSessionEntries((current) => [...current, createEntry(role, content, label)]);
  };

  const requestBridgeInput = (promptText: string, options?: { secret?: boolean }) => new Promise<string>((resolve) => {
    pendingBridgePromptResolverRef.current = resolve;
    setPendingBridgePrompt({ prompt: promptText, secret: options?.secret });
    appendEntry('tool', `MapleOS is waiting for operator input: ${promptText}`, 'input');
  });

  const resetSessionEntries = () => {
    const nextEntries = [
      createEntry('system', 'DeMythos transcript cleared.', 'session'),
      createEntry('system', 'Type /help for commands, or type a prompt directly to start a reasoning run.', 'session'),
    ];

    if (initialPrompt?.trim()) {
      nextEntries.push(createEntry('system', `Boot prompt loaded: ${initialPrompt.trim()}`, 'boot'));
    }

    if (activeRun) {
      const cutoff = Date.now();
      setClearedTranscriptCutoffs((current) => ({
        ...current,
        [activeRun.id]: cutoff,
      }));
    }

    setSessionEntries(nextEntries);
  };

  const rememberInput = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;

    const nextHistory = [...inputHistoryRef.current.filter((entry) => entry !== trimmed), trimmed].slice(-MAX_INPUT_HISTORY);
    inputHistoryRef.current = nextHistory;
    persistInputHistory(nextHistory);
  };

  const createKnowledgeBaseFromRun = async (run: MapleMythosRunState, customName?: string, reason: 'manual' | 'auto' = 'manual') => {
    const knowledgeBaseName = customName?.trim() || buildMapleMythosKnowledgeBaseName(run);

    setIsSavingKnowledgeBase(true);
    try {
      const knowledgeBaseId = await createKnowledgeBase(
        knowledgeBaseName,
        buildMapleMythosKnowledgeBaseDescription(run),
        undefined,
        true,
      );

      if (!knowledgeBaseId) {
        deMythosRuntime.updateRunKnowledgeBase(
          run.id,
          {
            knowledgeBaseSaveStatus: 'failed',
            knowledgeBaseName,
            knowledgeBaseError: 'Failed to create the knowledge base container.',
          },
          reason === 'auto' ? `Knowledge base auto-save failed for run ${run.id}.` : undefined,
        );
        appendEntry('tool', `Failed to create a knowledge base for run ${run.id}.`, 'kb');
        return null;
      }

      const documents = buildMapleMythosKnowledgeBaseDocuments(run);
      for (const document of documents) {
        const created = await addKnowledgeBaseDocument(knowledgeBaseId, document);
        if (!created) {
          deMythosRuntime.updateRunKnowledgeBase(
            run.id,
            {
              knowledgeBaseSaveStatus: 'failed',
              knowledgeBaseId,
              knowledgeBaseName,
              knowledgeBaseError: 'One or more knowledge base documents failed to save.',
            },
            reason === 'auto' ? `Knowledge base auto-save failed for run ${run.id}.` : undefined,
          );
          appendEntry('tool', `Created knowledge base ${knowledgeBaseName}, but one or more documents failed to save.`, 'kb');
          setLastKnowledgeBaseName(knowledgeBaseName);
          return knowledgeBaseId;
        }
      }

      deMythosRuntime.updateRunKnowledgeBase(
        run.id,
        {
          knowledgeBaseSaveStatus: 'saved',
          knowledgeBaseId,
          knowledgeBaseName,
          knowledgeBaseError: undefined,
          knowledgeBaseSavedAt: new Date().toISOString(),
        },
        reason === 'auto' ? `Knowledge base auto-saved as "${knowledgeBaseName}".` : undefined,
      );
      setLastKnowledgeBaseName(knowledgeBaseName);
      appendEntry(
        'tool',
        `${reason === 'auto' ? 'Automatically created' : 'Created'} knowledge base "${knowledgeBaseName}" from run ${run.id}.`,
        'kb',
      );
      return knowledgeBaseId;
    } finally {
      setIsSavingKnowledgeBase(false);
    }
  };

  useEffect(() => {
    for (const runId of Array.from(autoSavePendingRunIdsRef.current)) {
      const run = runs.find((candidate) => candidate.id === runId);
      if (!run || !FINAL_RUN_STATUSES.has(run.status)) {
        continue;
      }

      if (autoSaveInFlightRunIdsRef.current.has(runId)) {
        continue;
      }

      autoSavePendingRunIdsRef.current.delete(runId);
      autoSaveInFlightRunIdsRef.current.add(runId);
      void createKnowledgeBaseFromRun(run, undefined, 'auto').finally(() => {
        autoSaveInFlightRunIdsRef.current.delete(runId);
      });
    }
  }, [runs]);

  const startRun = async (nextPrompt?: string) => {
    if (!selectedModelId || !models.length) {
      appendEntry('tool', 'No AI model is configured for DeMythos yet.', 'status');
      return;
    }

    const effectivePrompt = buildActivePrompt(nextPrompt || prompt, seededPrompt);

    setPrompt(effectivePrompt);
    setIsLoading(true);

    try {
      const created = await deMythosRuntime.startRun(
        {
          prompt: effectivePrompt,
          modelId: selectedModelId,
          maxLoops,
          reasoningMode,
          returnMode,
          stopOnConvergence: !ultraEnabled,
          budget: {
            tokenBudget: tokenBudget ? Number(tokenBudget) : undefined,
            creditBudget: creditBudget ? Number(creditBudget) : undefined,
            timeBudgetMs: timeBudgetSeconds ? Number(timeBudgetSeconds) * 1000 : undefined,
          },
          knowledgeBaseAutoSave,
          origin: 'terminal',
          title: 'DeMythos Terminal Session',
        },
        buildRuntimeDependencies(toSshGrant(grantedSshProfile))
      );

      setActiveRunId(created.id);
      appendEntry('tool', `Started run ${created.id} using ${created.modelName}.${knowledgeBaseAutoSave ? ' A private knowledge base will be created automatically when the run finishes.' : ''}`, 'run');
    } catch (error) {
      appendEntry('tool', error instanceof Error ? error.message : 'DeMythos failed to start the run.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const exportTranscript = async () => {
    if (!activeRun) {
      appendEntry('tool', 'No run available to export.', 'export');
      return;
    }

    const payload = deMythosRuntime.exportRun(activeRun.id);
    try {
      await navigator.clipboard.writeText(payload);
      appendEntry('tool', 'Transcript copied to clipboard.', 'export');
    } catch {
      appendEntry('tool', payload, 'export');
    }
  };

  const handleCommand = async (value: string) => {
    const parsed = parseDeMythosCommand(value);
    if (!parsed) return;

    appendEntry('user', `demythos> ${value}`, 'command');

    if (parsed.type === 'unknown') {
      appendEntry('tool', parsed.message, 'error');
      return;
    }

    if (parsed.type === 'help') {
      appendEntry('tool', DEMYTHOS_HELP_LINES, 'help');
      return;
    }

    if (parsed.type === 'clear') {
      resetSessionEntries();
      return;
    }

    if (parsed.type === 'ssh') {
      if (parsed.action === 'list') {
        if (!sshProfiles.length) {
          appendEntry('tool', 'No saved SSH profiles were found in MapleOS Terminal settings.', 'ssh');
          return;
        }

        appendEntry(
          'tool',
          sshProfiles
            .map((profile) => `${profile.name} · ${profile.username}@${profile.host}:${profile.port} · ${profile.authType}`)
            .join('\n'),
          'ssh'
        );
        return;
      }

      if (parsed.action === 'off') {
        setGrantedSshProfileId('');
        appendEntry('tool', 'DeMythos SSH access removed.', 'ssh');
        return;
      }

      if (parsed.action === 'status') {
        const statusLines = [
          `Granted SSH profile: ${grantedSshProfile ? formatSshGrant(toSshGrant(grantedSshProfile)) : 'none'}`,
          `Terminal bridge: ${runTerminalCommand && submitTerminalInput ? 'available' : 'unavailable'}`,
        ];
        appendEntry('tool', statusLines.join('\n'), 'ssh');
        return;
      }

      const profile = parsed.reference ? findSshProfileByReference(sshProfiles, parsed.reference) : null;
      if (!profile) {
        appendEntry('tool', `Could not find SSH profile: ${parsed.reference}`, 'error');
        return;
      }

      setGrantedSshProfileId(profile.id);
      appendEntry(
        'tool',
        `DeMythos SSH grant set to ${profile.name} (${profile.username}@${profile.host}:${profile.port}).${profile.authType === 'password' ? ' MapleOS may ask you for the password during loop execution.' : ''}`,
        'ssh'
      );
      return;
    }

    if (parsed.type === 'kb') {
      if (parsed.action === 'status') {
        appendEntry(
          'tool',
          [
            `Knowledge base auto-save: ${knowledgeBaseAutoSave ? 'on' : 'off'}`,
            `Last generated knowledge base: ${lastKnowledgeBaseName || 'none'}`,
            `Active run: ${activeRun ? activeRun.id : 'none'}`,
            `Active run KB status: ${activeRun ? activeRun.knowledgeBaseSaveStatus : 'n/a'}`,
            activeRun?.knowledgeBaseName ? `Active run KB name: ${activeRun.knowledgeBaseName}` : null,
            activeRun?.knowledgeBaseError ? `Active run KB error: ${activeRun.knowledgeBaseError}` : null,
          ].filter(Boolean).join('\n'),
          'kb',
        );
        return;
      }

      if (parsed.action === 'auto') {
        const enabled = Boolean(parsed.enabled);
        setKnowledgeBaseAutoSave(enabled);
        appendEntry('tool', `Knowledge base auto-save ${enabled ? 'enabled' : 'disabled'}.`, 'kb');
        return;
      }

      const runToSave = activeRun || runs[0] || null;
      if (!runToSave) {
        appendEntry('tool', 'No run is available to save into a knowledge base.', 'kb');
        return;
      }

      await createKnowledgeBaseFromRun(runToSave, parsed.name, 'manual');
      return;
    }

    if (parsed.type === 'ultra') {
      setMaxLoops(ULTRA_MAX_LOOPS);
      setTokenBudget('');
      setCreditBudget('');
      setTimeBudgetSeconds('');
      setUltraModeEnabled(true);

      if (activeRun) {
        deMythosRuntime.updateRunConfig(activeRun.id, {
          maxLoops: ULTRA_MAX_LOOPS,
          stopOnConvergence: false,
          budget: {
            tokenBudget: 0,
            creditBudget: 0,
            timeBudgetMs: 0,
          },
        });
      }

      appendEntry(
        'tool',
        `Ultra mode enabled. Token, credit, and time budgets are cleared, loop ceiling is set to ${ULTRA_MAX_LOOPS}, and convergence auto-stop is disabled${activeRun ? ' for the active run and future runs.' : ' for future runs.'}`,
        'status'
      );
      return;
    }

    if (parsed.type === 'run') {
      if (parsed.prompt) {
        setPrompt(parsed.prompt);
      }
      await startRun(parsed.prompt);
      return;
    }

    if (parsed.type === 'model') {
      const exists = models.some((model) => model.id === parsed.modelId);
      if (!exists) {
        appendEntry('tool', `Unknown model id: ${parsed.modelId}`, 'error');
        return;
      }
      setSelectedModelId(parsed.modelId);
      appendEntry('tool', `Selected model: ${parsed.modelId}`, 'status');
      return;
    }

    if (parsed.type === 'loops') {
      setMaxLoops(parsed.value);
      setUltraModeEnabled(false);
      if (activeRun) {
        deMythosRuntime.updateRunConfig(activeRun.id, { maxLoops: parsed.value });
      }
      appendEntry('tool', `Loop budget set to ${parsed.value}. Ultra mode disabled.`, 'status');
      return;
    }

    if (parsed.type === 'mode') {
      setReasoningMode(parsed.value);
      if (activeRun) {
        deMythosRuntime.updateRunConfig(activeRun.id, { reasoningMode: parsed.value });
      }
      appendEntry('tool', `Reasoning mode set to ${parsed.value}.`, 'status');
      return;
    }

    if (parsed.type === 'return') {
      setReturnMode(parsed.value);
      appendEntry('tool', `Return mode set to ${parsed.value}.`, 'status');
      return;
    }

    if (parsed.type === 'budget') {
      if (parsed.budget.tokenBudget !== undefined) setTokenBudget(String(parsed.budget.tokenBudget));
      if (parsed.budget.creditBudget !== undefined) setCreditBudget(String(parsed.budget.creditBudget));
      if (parsed.budget.timeBudgetMs !== undefined) setTimeBudgetSeconds(String(parsed.budget.timeBudgetMs / 1000));
      setUltraModeEnabled(false);
      if (activeRun) {
        deMythosRuntime.updateRunConfig(activeRun.id, { budget: parsed.budget });
      }
      appendEntry('tool', 'Budget updated. Ultra mode disabled.', 'status');
      return;
    }

    if (parsed.type === 'status') {
      appendEntry('tool', buildStatusLine(activeRun, activeRunModel, grantedSshProfile), 'status');
      return;
    }

    if (!activeRun) {
      appendEntry('tool', 'No active DeMythos run.', 'error');
      return;
    }

    if (parsed.type === 'pause') {
      deMythosRuntime.pauseRun(activeRun.id);
      appendEntry('tool', 'Pause requested.', 'control');
      return;
    }

    if (parsed.type === 'resume') {
      deMythosRuntime.attachRunDependencies(
        activeRun.id,
        buildRuntimeDependencies(activeRun.sshGrant || toSshGrant(grantedSshProfile))
      );
      const resumedRun = deMythosRuntime.resumeRun(activeRun.id);
      appendEntry('tool', resumedRun.status === 'running' ? 'Run resumed.' : `This run cannot be resumed from status "${resumedRun.status}".`, 'control');
      return;
    }

    if (parsed.type === 'stop') {
      deMythosRuntime.stopRun(activeRun.id);
      appendEntry('tool', 'Stop requested.', 'control');
      return;
    }

    if (parsed.type === 'delete') {
      try {
        const remainingRuns = deMythosRuntime.deleteRun(activeRun.id);
        setClearedTranscriptCutoffs((current) => {
          const next = { ...current };
          delete next[activeRun.id];
          return next;
        });
        setActiveRunId(remainingRuns[0]?.id || null);
        resetSessionEntries();
        appendEntry('tool', `Deleted run ${activeRun.id}. Start a new run with /run <prompt> or just type a prompt.`, 'control');
      } catch (error) {
        appendEntry('tool', error instanceof Error ? error.message : 'Unable to delete the active run.', 'error');
      }
      return;
    }

    if (parsed.type === 'transcript') {
      await exportTranscript();
      return;
    }

    if (parsed.type === 'result') {
      appendEntry('tool', summarizeResult(activeRun), 'result');
    }
  };

  const submitPrompt = async (overrideValue?: string) => {
    const nextValue = overrideValue ?? input;
    if (!nextValue.trim() || isLoading || isSavingKnowledgeBase) return;

    if (pendingBridgePromptResolverRef.current && pendingBridgePrompt) {
      const resolver = pendingBridgePromptResolverRef.current;
      pendingBridgePromptResolverRef.current = null;
      setPendingBridgePrompt(null);
      setInput('');
      appendEntry('tool', pendingBridgePrompt.secret ? `${pendingBridgePrompt.prompt}[hidden]` : `${pendingBridgePrompt.prompt}${nextValue}`, 'input');
      resolver(nextValue);
      window.setTimeout(() => inputRef.current?.focus(), 0);
      return;
    }

    const trimmed = nextValue.trim();
    rememberInput(trimmed);
    historyDraftRef.current = '';
    setHistoryIndex(-1);
    setInput('');

    await handleCommand(trimmed);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      void submitPrompt();
      return;
    }

    if (pendingBridgePrompt) {
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      const history = inputHistoryRef.current;
      if (!history.length) return;

      const nextIndex = historyIndex === -1 ? history.length - 1 : Math.max(0, historyIndex - 1);
      if (historyIndex === -1) {
        historyDraftRef.current = input;
      }
      setHistoryIndex(nextIndex);
      setInput(history[nextIndex] || '');
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const history = inputHistoryRef.current;
      if (!history.length || historyIndex === -1) return;

      const nextIndex = historyIndex + 1;
      if (nextIndex >= history.length) {
        setHistoryIndex(-1);
        setInput(historyDraftRef.current);
        return;
      }

      setHistoryIndex(nextIndex);
      setInput(history[nextIndex] || '');
      return;
    }

    if (event.ctrlKey && (event.key === 'c' || event.key === 'C')) {
      event.preventDefault();
      appendEntry('tool', 'Exiting DeMythos.', 'session');
      onExit?.();
    }
  };

  return (
    <div className="flex h-full flex-col text-slate-100">
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md border border-emerald-400/20 bg-emerald-400/10 text-emerald-300">
            <TerminalSquare className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="font-mono text-sm font-semibold uppercase tracking-[0.2em] text-emerald-300">DeMythos</h2>
              <Badge variant="outline" className="border-white/10 bg-white/5 text-[10px] text-slate-300">
                {activeRun?.status || 'idle'}
              </Badge>
              <Badge variant="outline" className="border-white/10 bg-white/5 text-[10px] text-slate-300">
                loops {activeRun?.currentLoop || 0}/{activeRun?.maxLoops || maxLoops}
              </Badge>
              <Badge variant="outline" className="border-white/10 bg-white/5 text-[10px] text-slate-300">
                {selectedModel?.name || 'no model'}
              </Badge>
              <Badge variant="outline" className="border-white/10 bg-white/5 text-[10px] text-slate-300">
                ssh {grantedSshProfile?.name || 'off'}
              </Badge>
              <Badge variant="outline" className="border-white/10 bg-white/5 text-[10px] text-slate-300">
                kb {knowledgeBaseAutoSave ? 'auto' : 'manual'}
              </Badge>
            </div>
            <p className="truncate text-xs text-slate-400" title={buildStatusLine(activeRun, activeRunModel, grantedSshProfile)}>
              {buildStatusLine(activeRun, activeRunModel, grantedSshProfile)}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="hidden font-mono text-[11px] text-slate-500 lg:block">
            {sessionMeta.timestamp} · {sessionMeta.elapsed} · {sessionMeta.messageCount} events
          </div>
          <Button
            variant="outline"
            className="border-white/10 bg-white/5 text-slate-100 hover:bg-white/10"
            onClick={() => setIsStatusPaneOpen((current) => !current)}
          >
            {isStatusPaneOpen ? 'Hide status' : 'Show status'}
          </Button>
          <Select value={selectedModel?.id || ''} onValueChange={setSelectedModelId}>
            <SelectTrigger className="w-[220px] border-white/10 bg-white/5 text-slate-100">
              <SelectValue placeholder="Select model" />
            </SelectTrigger>
            <SelectContent>
              {models.map((model) => (
                <SelectItem key={model.id} value={model.id}>
                  {model.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {onExit && (
            <Button variant="outline" className="border-white/10 bg-white/5 text-slate-100 hover:bg-white/10" onClick={onExit}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Exit
            </Button>
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1 p-4">
        <Card className="flex h-full min-h-0 flex-col overflow-hidden border-white/10 bg-black/30 shadow-none">
          <div className="min-h-0 flex flex-1">
            <div className={`flex min-h-0 min-w-0 flex-1 flex-col ${isStatusPaneOpen ? 'border-r border-white/10' : ''}`}>
              <ScrollArea className="min-h-0 flex-1">
                <div className="space-y-4 p-4 font-mono text-sm">
                  {sessionEntries.map((entry) => (
                    <div key={entry.id} className="space-y-1">
                      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-slate-500">
                        <span>{entry.label || entry.role}</span>
                      </div>
                      <div
                        className={[
                          'rounded-lg border px-3 py-2 leading-6',
                          entry.role === 'user' && 'border-blue-400/20 bg-blue-400/5 text-slate-100',
                          entry.role === 'tool' && 'border-amber-400/20 bg-amber-400/5 text-slate-200',
                          entry.role === 'system' && 'border-white/10 bg-white/5 text-slate-300',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        {entry.role === 'tool' ? (
                          <pre className="overflow-x-auto whitespace-pre-wrap break-words font-mono text-sm leading-6 text-inherit">
                            {entry.content}
                          </pre>
                        ) : (
                          <ConsoleMarkdown content={entry.content} tone={entry.role === 'user' ? 'user' : 'system'} />
                        )}
                      </div>
                    </div>
                  ))}

                  {visibleRunTranscript.map((entry) => (
                    <div key={entry.id} className="space-y-1">
                      <div className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-slate-500">
                        <span>{entry.phase || entry.kind}</span>
                        {typeof entry.loopIndex === 'number' && <span>loop {entry.loopIndex}</span>}
                        <span>{new Date(entry.timestamp).toLocaleTimeString()}</span>
                      </div>
                      <div className="rounded-lg border border-emerald-400/20 bg-emerald-400/5 px-3 py-2 text-slate-100">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="whitespace-pre-wrap break-words">{entry.message}</div>
                          {activePhaseEntryIds.has(entry.id) && (
                            <span className="inline-flex items-center text-xs text-emerald-300/80">
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            </span>
                          )}

                        </div>
                        {entry.content && (
                          <pre className="mt-3 overflow-x-auto whitespace-pre-wrap break-words rounded-md border border-white/10 bg-black/30 p-3 font-mono text-sm leading-6 text-slate-300">
                            {entry.content}
                          </pre>
                        )}
                        {(entry.tokensUsed || entry.creditsUsed) && (
                          <div className="mt-2 text-xs text-slate-500">
                            tokens {entry.tokensUsed || 0} · credits {activeRunModel && !isBillableMapleMythosModel(activeRunModel) ? 'non-billable' : formatCreditAmount(entry.creditsUsed || 0)}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {(isLoading || isSavingKnowledgeBase) && (
                    <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 font-mono text-sm text-slate-300">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>
                        {isSavingKnowledgeBase
                          ? 'DeMythos is writing a knowledge base from the current findings…'
                          : (
                            <>
                              {thinkingFrame.before}
                              <span className="text-emerald-300">{thinkingFrame.shimmer}</span>
                              {thinkingFrame.after}
                            </>
                          )}
                      </span>
                      <span className="ml-auto text-xs text-slate-500">{sessionMeta.elapsed}</span>
                    </div>
                  )}
                  <div ref={transcriptEndRef} />
                </div>
              </ScrollArea>

              <div className="border-t border-white/10 p-4">
                <div className="mb-2 flex items-center justify-between text-xs text-slate-400">
                  <span>Type a prompt to start a run, or use /help for DeMythos commands.</span>
                  <span>Ctrl+C exits · ↑/↓ history</span>
                </div>
                {pendingBridgePrompt && (
                  <div className="mb-3 rounded-md border border-cyan-400/20 bg-cyan-400/10 px-3 py-2 font-mono text-sm text-cyan-100">
                    <div className="mb-1 text-[11px] uppercase tracking-[0.18em] text-cyan-300">Operator input required</div>
                    <div className="whitespace-pre-wrap break-words">{pendingBridgePrompt.prompt}</div>
                  </div>
                )}
                <form
                  className="flex items-center gap-2"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void submitPrompt();
                  }}
                >
                  <div className="flex h-10 items-center rounded-md border border-white/10 bg-white/5 px-3 text-emerald-300">
                    <TerminalSquare className="h-4 w-4" />
                  </div>
                  <Input
                    ref={inputRef}
                    type={pendingBridgePrompt?.secret ? 'password' : 'text'}
                    value={input}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      setInput(nextValue);
                      if (!pendingBridgePrompt && historyIndex !== -1) {
                        setHistoryIndex(-1);
                      }
                      historyDraftRef.current = nextValue;
                    }}
                    onKeyDown={handleInputKeyDown}
                    placeholder={pendingBridgePrompt
                      ? (pendingBridgePrompt.secret ? 'Hidden input…' : 'Respond and press Enter…')
                      : 'Ask DeMythos something, or run /status, /ssh, /kb save…'}
                    className="border-white/10 bg-white/5 font-mono text-slate-100 placeholder:text-slate-500"
                    disabled={isLoading || isSavingKnowledgeBase}
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <Button type="submit" disabled={isLoading || isSavingKnowledgeBase || !input.trim()}>
                    {pendingBridgePrompt ? 'Reply' : 'Send'}
                  </Button>
                </form>
              </div>
            </div>

            <div
              className={[
                'min-h-0 overflow-hidden transition-all duration-300 ease-out',
                isStatusPaneOpen
                  ? 'w-[36%] min-w-[300px] max-w-[520px] opacity-100'
                  : 'w-0 min-w-0 opacity-0',
              ].join(' ')}
            >
              <div className="flex h-full min-h-0 flex-col bg-black/60">
                <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
                  <div>
                    <div className="font-mono text-xs font-semibold uppercase tracking-[0.18em] text-cyan-300">Run status</div>
                    <div className="text-xs text-slate-500">DeMythos session controls</div>
                  </div>
                  <Badge variant="outline" className="border-white/10 bg-white/5 text-[10px] text-slate-300">
                    {runs.length} runs
                  </Badge>
                </div>

                <ScrollArea className="min-h-0 flex-1">
                  <div className="space-y-4 p-4 font-mono text-sm">
                    <div className="rounded-lg border border-white/10 bg-white/5 p-3 text-slate-300">
                      <div className="mb-2 text-[11px] uppercase tracking-[0.18em] text-slate-500">Current config</div>
                      <div>model: {selectedModel?.name || 'none'}</div>
                      <div>reasoning: {reasoningMode}</div>
                      <div>return: {returnMode}</div>
                      <div>max loops: {maxLoops}</div>
                      <div>token budget: {tokenBudget || '—'}</div>
                      <div>credit budget: {creditBudget || '—'}</div>
                      <div>time budget: {formatTimeBudget(timeBudgetSeconds ? Number(timeBudgetSeconds) * 1000 : undefined)}</div>
                      <div>execution: {activeRun?.executionMode || (selectedModel && cloudProviders.has(selectedModel.provider) ? 'cloud' : 'local')}</div>
                      <div>ultra mode: {ultraEnabled ? 'on' : 'off'}</div>
                      <div>ssh grant: {grantedSshProfile ? formatSshGrant(toSshGrant(grantedSshProfile)) : 'none'}</div>
                      <div>terminal bridge: {runTerminalCommand && submitTerminalInput ? 'available' : 'unavailable'}</div>
                      <div>knowledge base auto-save: {knowledgeBaseAutoSave ? 'on' : 'off'}</div>
                      <div>last generated KB: {lastKnowledgeBaseName || 'none'}</div>
                      <div className="mt-2 text-xs text-slate-500">Use /ssh &lt;profile&gt; to grant a saved MapleOS SSH profile. Use /kb auto on to automatically create a private knowledge base from each completed run, including findings and terminal output.</div>
                    </div>

                    <div className="rounded-lg border border-white/10 bg-white/5 p-3 text-slate-300">
                      <div className="mb-2 text-[11px] uppercase tracking-[0.18em] text-slate-500">Prompt</div>
                      <div className="whitespace-pre-wrap break-words text-slate-200">{prompt}</div>
                    </div>

                    <div className="rounded-lg border border-white/10 bg-white/5 p-3 text-slate-300">
                      <div className="mb-2 text-[11px] uppercase tracking-[0.18em] text-slate-500">Latest result</div>
                      <pre className="overflow-x-auto whitespace-pre-wrap break-words font-mono text-sm leading-6 text-slate-200">
                        {visibleLatestResult}
                      </pre>
                    </div>

                    <div className="rounded-lg border border-white/10 bg-white/5 p-3 text-slate-300">
                      <div className="mb-2 text-[11px] uppercase tracking-[0.18em] text-slate-500">Quick commands</div>
                      <pre className="overflow-x-auto whitespace-pre-wrap break-words font-mono text-sm leading-6 text-slate-200">
                        {DEMYTHOS_HELP_LINES}
                      </pre>
                    </div>
                  </div>
                </ScrollArea>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}